import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Product } from '../../stores/appReportsStore';
import ProductCard from './ProductCard';
import { Loader2, PencilLine, X } from 'lucide-react';
import { calculateTotalSpent } from '../../services/import/importProductIdsService';
import { currencyFormatter } from '../../services/helperService';
import { cardResponsiveWidth } from '../../styling/stylingHelper';

interface ComparisonPairProps {
  pair: { left: Product; right?: Product };
  loading: boolean;
  onQuantityChange: (id: string, v: string) => void;
  onEdit: (id: string) => void;
  onDeleteProduct?: (id: string) => void;
}

export default function ComparisonPair({
  pair: { left, right },
  loading,
  onQuantityChange,
  onEdit,
  onDeleteProduct,
}: ComparisonPairProps) {
  const { t } = useTranslation();

  const noDefaultInputStyling =
    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

  const totalLeft = calculateTotalSpent(left);
  const totalRight = right ? calculateTotalSpent(right) : 0;
  const savingsAmt = right ? totalRight - totalLeft : 0;
  const savingsText = currencyFormatter.format(Math.abs(savingsAmt));
  const savingsPct = right ? (savingsAmt / totalLeft) * 100 : 0;

  const pctClass =
    savingsPct === 0
      ? 'text-muted dark:text-muted-dark'
      : savingsPct < 0
      ? 'text-textGreen dark:text-textGreen-dark'
      : 'text-textRed dark:text-textRed-dark';

  // Middle column: product cards (and savings if right exists)
  const midClasses = right
    ? 'flex flex-col min-[1265px]:flex-row flex-1 items-center justify-center gap-4'
    : 'flex flex-col min-[1265px]:flex-row items-center gap-4';

  return (
    <div className="relative bg-background dark:bg-background-dark flex flex-col min-[1265px]:flex-row gap-2 rounded-xl px-4 py-2 max-[1265px]:max-w-[600px] max-[1265px]:mx-auto">
      {!loading && (
        <button
          onClick={() => onDeleteProduct?.(left.articleId)}
          aria-label={t('actions.delete')}
          className="absolute top-1 right-2 p-1"
        >
          <X className="w-4 h-4 text-tertiary dark:text-tertiary-dark hover:scale-90 transition-transform duration-200" />
        </button>
      )}

      <div className="flex flex-col min-[1265px]:flex-row items-center w-full p-2">
        {/* 1 — quantity */}
        <input
          type="number"
          min={1}
          value={Number.isInteger(left.quantity) ? left.quantity.toString() : left.quantity.toFixed(1)}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onQuantityChange(left.articleId, e.target.value)
          }
          className={`hidden min-[1265px]:block ${noDefaultInputStyling} mb-0 mr-3 w-12 h-8 px-1 text-center text-base border border-borderGrey dark:border-borderGrey-dark rounded-lg font-semibold text-primary dark:text-primary-dark focus:outline-none`}
          aria-label={t('sourcing.quantity')}
        />
        <div className={`flex flex-row min-[1265px]:hidden w-full items-center gap-2 mb-4 mx-2 mr-4 ${cardResponsiveWidth}`}>
          <span className="w-fit ml-2 mr-1 font-medium text-base text-secondary dark:text-secondary-dark">
            {t('product.quantity')}
          </span>
          <div className="px-2 flex-1">
            <input
              type="number"
              min={1}
              value={Number.isInteger(left.quantity) ? left.quantity.toString() : left.quantity.toFixed(1)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onQuantityChange(left.articleId, e.target.value)
              }
              className={`${noDefaultInputStyling} w-full h-8 text-center text-base border border-borderGrey dark:border-borderGrey-dark rounded-lg font-semibold text-primary dark:text-primary-dark focus:outline-none`}
              aria-label={t('sourcing.quantity')}
            />
          </div>
          
        </div>

        {/* 2 — product cards (+ savings) */}
        <div className={midClasses}>
          <div className="flex flex-col min-[1265px]:flex-row items-center shrink-0">
            <div className="ml-2">
                <ProductCard product={left} />
            </div>
            <img
                src="/icons/separator_side.png"
                alt=""
                className="w-4 h-1.5 flex-none hidden min-[1265px]:flex"
              />
            <img
                src="/icons/separator_top.png"
                alt=""
                className="w-1.5 h-4 flex-none flex min-[1265px]:hidden"
              />
            {!right && !loading && (
             <div className="h-[100px] flex-none min-[1265px]:hidden" />
             )}
            {right && (
              <>
              <div className="ml-2 min-[1265px]:mr-2 min-[1265px]:ml-0">
                <ProductCard product={right} />
              </div>
              </>
            )}
            {!right && loading && (
              <>
                <div className="h-[100px] flex-none flex min-[1265px]:hidden items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-muted dark:text-muted-dark" />
                </div>
                <div className={`${cardResponsiveWidth} flex-none hidden min-[1265px]:flex items-center justify-center`}>
                    <Loader2 className="w-5 h-5 animate-spin text-muted dark:text-muted-dark" />
                </div>
             </>
             )}
          </div>

          {right && (
            <>
            {/* desktop responsive */}
            <div className="flex-1 hidden min-[1265px]:flex justify-center mt-0 ml-1">
              <div className="max-w-16 p-2 flex flex-col items-end">
                <p className="text-right truncate text-primary dark:text-primary-dark font-semibold text-sm">
                  {savingsText}
                </p>
                <p className={`text-right truncate text-xs ${pctClass}`}>
                  {savingsPct.toFixed(0)}%
                </p>
              </div>
            </div>
            {/* mobile responsive */}
            <div className={`${cardResponsiveWidth} flex-1 flex justify-between mt-4 mx-2 min-[1265px]:hidden`}>
              <span className="font-medium text-base text-secondary dark:text-secondary-dark">
                {t('product.savings')}
              </span>
              <div className="flex flex-col items-end">
                <p className="text-right truncate text-primary dark:text-primary-dark font-semibold text-sm">
                  {savingsText}
                </p>
                <p className={`text-right truncate text-xs ${pctClass}`}>
                  {savingsPct.toFixed(0)}%
                </p>
              </div>
            </div>
            </>
          )}
        </div>

        {/* 3 — edit icon (fixed width) */}
        {right && (
          <>
            <button
              onClick={() => onEdit(left.articleId)}
              className="flex-none shrink-0 hidden min-[1265px]:flex mt-0 ml-4 items-center justify-center w-10 h-10 border border-textGreen dark:border-textGreen-dark rounded-full hover:bg-cardLightGreen dark:hover:bg-cardLightGreen-dark transition-transform duration-200"
            >
              <PencilLine className="w-5 h-5 text-textGreen dark:text-textGreen-dark" />
            </button>
            <button
              onClick={() => onEdit(left.articleId)}
              className={`${cardResponsiveWidth} flex-none shrink-0 mt-4 flex min-[1265px]:hidden items-center justify-center gap-2 border border-borderGrey dark:border-borderGrey-dark rounded-lg hover:bg-cardLightGreen dark:hover:bg-cardLightGreen-dark transition-transform duration-200 px-4 py-2`}
            >
              <PencilLine className="w-5 h-5 text-textGreen dark:text-textGreen-dark" />
              <span className="text-sm font-semibold text-textGreen dark:text-textGreen-dark ml-2">
                {t('product.options')}
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
